/**
 * describeCastGeometry — an ability's visible geometry, described from the
 * builder configs its local behaviours run on (plan 29 F5, lane BV).
 *
 * Stored on the registry metadata (`metadata.castGeometry`) at build time and
 * snapshotted for observers (src/game/effects/castGeometry/castGeometry.gen.ts).
 * Each `*GeometryOf` here is also what the matching behaviour computes its
 * visuals from, so the description and the caster's own pixels cannot part.
 */
import type { AOEConfig } from '../behaviors/AOEBehavior';
import type { SummonConfig } from '../behaviors/SummonBehavior';
import type { DashConfig } from '../behaviors/DashBehavior';
import type { CasterAuraConfig } from '../AbilityBuilderFactory';
import type {
  AreaGeometry, CastGeometry, ZoneGeometry, ConeGeometry, LineGeometry, OrbitGeometry, BeatGeometry,
} from '../../../../effects/castGeometry/castGeometry';
import { swingGeometryOf } from '../../../../effects/castGeometry/castVisualPlan';
import { resolveNovaLook } from '../../../../effects/castGeometry/novaLaunches';
import { getAbilityData } from '../../../../../shared/content/AbilityDataAdapter';
import { healShape } from '../../../../../shared/combat/friendlyCasts';

/** AOEBehavior's area (placement 'player'/absent → on the body). */
export function areaGeometryOf(c: AOEConfig): AreaGeometry {
  return {
    radius: c.radius,
    forwardPx: typeof c.placement === 'number' ? c.placement : 0,
    ...(c.vfxId ? { effectId: c.vfxId } : {}),
    ...(c.windUpVfxId ? { windUpEffectId: c.windUpVfxId } : {}),
    windUpMs: c.windUpMs ?? 0,
    ticks: c.ticks ?? 1,
    intervalMs: c.intervalMs ?? 500,
  };
}

/** SummonBehavior's placement: 'atCaster' on the body, a number, else 80 px ahead. */
export function zoneGeometryOf(c: SummonConfig): ZoneGeometry {
  return {
    kind: c.type,
    radius: c.radius,
    forwardPx: c.placement === 'atCaster' ? 0 : typeof c.placement === 'number' ? c.placement : 80,
    durationMs: c.duration,
    ...(c.vfxId ? { effectId: c.vfxId } : {}),
    ...(c.vfxTrigger ? { triggerEffectId: c.vfxTrigger } : {}),
    trigger: c.trigger ?? 'proximity',
    triggerDelayMs: c.triggerDelay ?? 0,
  };
}

/**
 * An area heal's splash (HealBehavior, plan 29 F6 + bug 10): at the body
 * centre at exactly the TOML radius, ringed for 600 ms. Undefined for a
 * targeted heal (it lands on whoever receives it).
 */
export function healAreaGeometryOf(abilityId: string, vfxId: string): AreaGeometry | undefined {
  const data = getAbilityData(abilityId);
  if (healShape(data) !== 'area' || !data?.radius) return undefined;
  return { radius: data.radius, forwardPx: 0, effectId: vfxId, windUpMs: 0, ticks: 1, intervalMs: HEAL_SPLASH_MS - 100, markerMs: HEAL_SPLASH_MS };
}

/** The splash an area heal draws (ms). */
export const HEAL_SPLASH_MS = 600;

/** A world-anchored aura (totems) is a placed zone; a caster aura is not geometry. */
export function worldAuraGeometryOf(c: NonNullable<GeometrySources['auraEffectConfig']>): ZoneGeometry | undefined {
  if (c.anchor !== 'world') return undefined;
  return {
    kind: c.summonType ?? 'zone', radius: c.radius, forwardPx: 0, durationMs: c.duration ?? 0,
    ...(c.vfxId ? { effectId: c.vfxId } : {}), trigger: 'proximity', triggerDelayMs: 0,
  };
}

/** The builder configs the description reads (a subset of BuilderConfigs). */
export interface GeometrySources {
  meleeConfig?: { arcAngle?: number; arcSpread?: number } | null;
  aoeConfig?: AOEConfig | null;
  summonConfig?: SummonConfig | null;
  coneAOEConfig?: { angle: number; distance: number; vfxId?: string } | null;
  lineAOEConfig?: { length: number; width: number; vfxId?: string } | null;
  dashConfig?: DashConfig | null;
  novaConfig?: Parameters<typeof resolveNovaLook>[0] | null;
  multiProjectiles?: { count: number; stagger?: number } | null;
  healConfig?: { vfxId?: string } | null;
  auraEffectConfig?: { radius: number; duration?: number; vfxId?: string; anchor?: 'caster' | 'world'; summonType?: 'trap' | 'totem' | 'zone' } | null;
}

/** Extras the factory knows: the caster aura, the withVFX beat, a declared orbit. */
export interface GeometryExtras {
  casterAura?: CasterAuraConfig | null;
  beat?: { effectId?: string; duration?: number; color?: number; scale?: number } | null;
  orbit?: OrbitGeometry | null;
  /** The factory's wind-up wrapper delay: every visual starts after it. */
  windUpMs?: number;
  /** The wind-up wrapper's tell, drawn on the caster for `windUpMs`. */
  windUpEffectId?: string | null;
  /** The ability's projectile metadata (a nova's shape/size fallbacks). */
  projectile?: { shape?: string; color?: number; width?: number; height?: number } | null;
  /** The ability (its TOML decides an area heal's radius). */
  abilityId?: string;
}

export function describeCastGeometry(
  configs: GeometrySources,
  melee: { lineWidth?: number; swingDuration?: number; lineColor?: number } | undefined,
  extras: GeometryExtras = {},
): CastGeometry {
  const g: CastGeometry = {};
  if (extras.windUpMs && extras.windUpMs > 0) {
    g.castDelayMs = extras.windUpMs;
    if (extras.windUpEffectId) g.castDelayEffectId = extras.windUpEffectId;
  }
  if (extras.beat?.effectId) {
    const beat: BeatGeometry = { effectId: extras.beat.effectId, durationMs: extras.beat.duration ?? 400 };
    if (extras.beat.color !== undefined) beat.color = extras.beat.color;
    if (extras.beat.scale !== undefined) beat.scale = extras.beat.scale;
    g.beat = beat;
  }
  if (extras.casterAura) {
    g.casterAura = { effectId: extras.casterAura.effectId, durationMs: extras.casterAura.durationMs, ...(extras.casterAura.color !== undefined ? { color: extras.casterAura.color } : {}) };
  }
  if (extras.orbit) g.orbit = { ...extras.orbit };
  if (configs.meleeConfig) g.swing = swingGeometryOf(configs.meleeConfig, melee);
  if (configs.aoeConfig) g.area = areaGeometryOf(configs.aoeConfig);
  else if (configs.healConfig?.vfxId && extras.abilityId) {
    const area = healAreaGeometryOf(extras.abilityId, configs.healConfig.vfxId);
    if (area) g.area = area;
  }
  if (configs.summonConfig) g.zone = zoneGeometryOf(configs.summonConfig);
  else if (configs.auraEffectConfig) {
    // Only a timed aura: an observer cannot know when a persistent one ends.
    const zone = worldAuraGeometryOf(configs.auraEffectConfig);
    if (zone && zone.durationMs > 0) g.zone = zone;
  }
  if (configs.coneAOEConfig) {
    const c = configs.coneAOEConfig;
    const cone: ConeGeometry = { angle: c.angle, distance: c.distance };
    if (c.vfxId) cone.effectId = c.vfxId;
    g.cone = cone;
  }
  if (configs.lineAOEConfig) {
    const c = configs.lineAOEConfig;
    const line: LineGeometry = { length: c.length, width: c.width };
    if (c.vfxId) line.effectId = c.vfxId;
    g.line = line;
  }
  if (configs.dashConfig) {
    const d = configs.dashConfig;
    g.dash = {
      distance: d.distance, travelMs: d.travelMs ?? 0,
      ...(d.vfxOriginId ? { originEffectId: d.vfxOriginId } : {}),
      ...(d.vfxDestinationId ? { destinationEffectId: d.vfxDestinationId } : {}),
      ...(d.travelMode ? { mode: d.travelMode } : {}),
      ...(d.horizontalPolicy ? { horizontalPolicy: d.horizontalPolicy } : {}),
      ...(d.trailVfxId ? { trailEffectId: d.trailVfxId } : {}),
      // Empty string explicitly suppresses Heroic Leap's generic landing.
      ...(d.landingVfxId !== undefined ? { landingEffectId: d.landingVfxId } : {}),
      ...(d.ghosts ? { ghosts: { ...d.ghosts } } : {}),
    };
  }
  if (configs.novaConfig) g.nova = resolveNovaLook(configs.novaConfig, extras.projectile ?? undefined);
  else if (configs.multiProjectiles) g.volley = { count: configs.multiProjectiles.count, staggerMs: configs.multiProjectiles.stagger ?? 0 };
  return g;
}
